import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Shield, Clock, FileText, User, Calendar, Activity, Clipboard, Search, Bell, Settings, 
  Server, Database, Lock, Cloud, BarChart, CheckCircle, Heart, Zap, Brain, Target, AlertCircle, Timer, 
  Users, Bed, Stethoscope, TrendingUp, ArrowRight, X, Plus, RefreshCw, Filter, Eye, Download, Phone, 
  MessageCircle, UserCheck, Thermometer, Droplets, Wind, Loader2, Wifi, WifiOff, List, LogOut
} from 'lucide-react';

const EDDashboardV3 = ({ 
  patient, 
  patients = [], 
  totalPatients = 0,
  onPatientSelect,
  onViewPatientList,
  onLogout,
  user,
  apiBase,
  isMainWorkstation = false,
  onRefreshPatients,
  loading = false
}) => {
  const [activeTab, setActiveTab] = useState('ai-analysis');
  const [showMetrics, setShowMetrics] = useState(true);
  const [expandedSection, setExpandedSection] = useState('assessment');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState('healthcare-api');
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [showPatientSelector, setShowPatientSelector] = useState(false);
  const [sessionId] = useState(() => 
    `ED-${Math.random().toString(36).substr(2, 8).toUpperCase()}-${Date.now().toString().slice(-6)}`
  );

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const dataSourceConfig = {
    'healthcare-api': {
      name: 'Google Healthcare API',
      endpoint: 'https://healthcare.googleapis.com/v1/projects/healthcare-platform-1749107936/locations/us-central1/datasets/clinical-data/fhirStores/ed-fhir-store/fhir',
      description: 'FHIR R4 compliant Healthcare API'
    },
    'alloydb': {
      name: 'AlloyDB PostgreSQL',
      endpoint: 'postgresql://alloydb-cluster.internal:5432/ehr_clinical_db',
      description: 'High-performance PostgreSQL with FHIR mapping'
    }
  };

  const [fhirData, setFhirData] = useState({
    patient: { 
      resourceType: 'Patient', 
      id: patient?.id || null, 
      identifier: [{ value: patient?.id || '', system: 'http://hospital.com/mrn' }], 
      name: [{ given: [patient?.name?.split(' ')[0] || ''], family: patient?.name?.split(' ').slice(1).join(' ') || '' }], 
      gender: patient?.gender || '', 
      birthDate: patient?.birthDate || '', 
      active: true 
    },
    encounter: { 
      resourceType: 'Encounter', 
      id: null, 
      status: 'in-progress', 
      class: { code: 'EMER', display: 'emergency' }, 
      subject: { reference: `Patient/${patient?.id}` }, 
      period: { start: new Date().toISOString() }, 
      location: [{ location: { display: 'ED Bay 3' } }], 
      reasonCode: [{ text: patient?.condition || '' }] 
    },
    observations: [],
    riskAssessment: { 
      resourceType: 'RiskAssessment', 
      id: null, 
      status: 'final', 
      subject: { reference: `Patient/${patient?.id}` }, 
      encounter: { reference: '' }, 
      performedDateTime: null, 
      performer: { reference: 'Device/goatnote-ai-system' }, 
      prediction: [{ 
        outcome: { text: '' }, 
        probabilityDecimal: patient?.severity === 'Critical' ? 0.085 : patient?.severity === 'High' ? 0.065 : 0.035, 
        rationale: '' 
      }], 
      extension: [
        { url: 'http://goatnote.com/fhir/ai-confidence', valueDecimal: 0.92 },
        { url: 'http://goatnote.com/fhir/calibration-score', valueDecimal: 0.40 }
      ] 
    }
  });

  const [systemMetrics, setSystemMetrics] = useState({
    responseTime: '1.2s', 
    aiProcessingTime: '0.8s', 
    riskCalibration: '40%', 
    currentPatients: totalPatients.toString(), 
    bedOccupancy: '89%', 
    avgWaitTime: '2.3h', 
    criticalAlerts: '3', 
    aiModelVersion: 'MedLM 2.5.1 + Calibration', 
    lastUpdate: new Date().toLocaleTimeString(), 
    dataSource: dataSourceConfig[dataSource].name
  });

  // Update FHIR data when patient changes
  useEffect(() => {
    if (patient) {
      setFhirData(prev => ({
        ...prev,
        patient: { 
          ...prev.patient,
          id: patient.id || null, 
          identifier: [{ value: patient.id || '', system: 'http://hospital.com/mrn' }], 
          name: [{ given: [patient.name?.split(' ')[0] || ''], family: patient.name?.split(' ').slice(1).join(' ') || '' }], 
          gender: patient.gender || '', 
        },
        encounter: {
          ...prev.encounter,
          subject: { reference: `Patient/${patient.id}` },
          reasonCode: [{ text: patient.condition || '' }]
        },
        riskAssessment: {
          ...prev.riskAssessment,
          subject: { reference: `Patient/${patient.id}` },
          prediction: [{ 
            outcome: { text: '' }, 
            probabilityDecimal: patient.severity === 'Critical' ? 0.085 : patient.severity === 'High' ? 0.065 : 0.035, 
            rationale: '' 
          }]
        }
      }));
    }
  }, [patient]);

  // Helper functions
  const getPatientName = () => {
    return patient?.name || 'No Patient Selected';
  };

  const getPatientAge = () => {
    if (!patient?.age && !patient?.birthDate) return '--';
    if (patient?.age) return patient.age;
    if (patient?.birthDate) {
      const birthDate = new Date(patient.birthDate);
      return Math.floor((new Date() - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
    }
    return '--';
  };

  const getMRN = () => patient?.id || '--';
  const getRiskScore = () => fhirData.riskAssessment.prediction?.[0]?.probabilityDecimal || 0;

  const getAIConfidence = () => {
    const extensions = fhirData.riskAssessment.extension || [];
    const confidenceExt = extensions.find(ext => ext.url?.includes('confidence'));
    return confidenceExt?.valueDecimal ? (confidenceExt.valueDecimal * 100).toFixed(1) : null;
  };

  const getCalibrationScore = () => {
    const extensions = fhirData.riskAssessment.extension || [];
    const calibrationExt = extensions.find(ext => ext.url?.includes('calibration'));
    return calibrationExt?.valueDecimal ? (calibrationExt.valueDecimal * 100).toFixed(1) : null;
  };

  const getESILevel = () => {
    const severity = patient?.severity?.toLowerCase();
    if (severity === 'critical') return 1;
    if (severity === 'high') return 2;
    if (severity === 'medium') return 3;
    if (severity === 'low') return 4;
    return 5;
  };

  const getESIColor = (level) => {
    const colors = { 
      1: 'bg-red-600 text-white', 
      2: 'bg-orange-500 text-white', 
      3: 'bg-yellow-500 text-black', 
      4: 'bg-green-500 text-white', 
      5: 'bg-blue-500 text-white' 
    };
    return colors[level] || 'bg-gray-500 text-white';
  };

  const getRiskColor = (score) => {
    if (score === 0) return 'bg-gray-100 text-gray-500 border-gray-200';
    if (score > 0.075) return 'bg-red-100 text-red-800 border-red-200';
    if (score > 0.05) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  // API functions
  const fetchPatientData = async (patientId) => {
    setIsLoading(true);
    try {
      // Simulate API call - in production, would call real backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setConnectionStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const performAIRiskAnalysis = async () => {
    if (!patient?.id) return;
    setIsLoading(true);
    try {
      // Simulate AI analysis - in production, would call real AI service
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update risk assessment with simulated results
      setFhirData(prev => ({
        ...prev,
        riskAssessment: {
          ...prev.riskAssessment,
          performedDateTime: new Date().toISOString(),
          prediction: [{
            outcome: { text: 'Emergency intervention recommended' },
            probabilityDecimal: Math.random() * 0.1 + 0.05, // Random between 5-15%
            rationale: 'Based on clinical indicators and AI analysis'
          }]
        }
      }));
    } catch (error) {
      console.error('Error performing AI risk analysis:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patient?.id) {
      fetchPatientData(patient.id);
    }
  }, [patient?.id]);

  const riskScore = getRiskScore();
  const esiLevel = getESILevel();

  const navItems = [
    { id: 'ai-analysis', icon: Brain, label: 'AI Clinical Analysis', badge: riskScore > 0.075 ? 'HIGH' : null, badgeColor: 'bg-red-500' },
    { id: 'fhir-data', icon: Database, label: 'FHIR Data', badge: 'R4', badgeColor: 'bg-blue-500' },
    { id: 'orders', icon: Clipboard, label: 'Orders', badge: '0', badgeColor: 'bg-orange-500' },
    { id: 'lab-results', icon: Activity, label: 'Lab Results', badge: '0', badgeColor: 'bg-green-500' },
    { id: 'imaging', icon: Eye, label: 'Imaging', badge: '0', badgeColor: 'bg-purple-500' },
    { id: 'procedures', icon: Stethoscope, label: 'Procedures', badge: '0', badgeColor: 'bg-indigo-500' },
    { id: 'history', icon: Calendar, label: 'Medical History', badge: null, badgeColor: '' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="px-4 py-2 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <span className="font-bold text-xl text-blue-800">GOAT<span className="text-blue-500">note</span></span>
              <div className="ml-2 text-xs text-blue-600 font-medium">ED Workstation</div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">HIPAA COMPLIANT • FHIR R4</div>
              <div className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium flex items-center">
                <Activity className="h-3 w-3 mr-1" />
                CALIBRATED AI {getCalibrationScore() ? `(${getCalibrationScore()}%)` : '(--%)'}
              </div>
              <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium flex items-center">
                <Database className="h-3 w-3 mr-1" />
                {dataSourceConfig[dataSource].name}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-xs text-gray-600">{currentTime.toLocaleTimeString()}</div>
            <div className={`px-2 py-1 rounded text-xs font-medium flex items-center ${connectionStatus === 'connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {connectionStatus === 'connected' ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
              {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
            </div>
            {isMainWorkstation && (
              <>
                <button onClick={onViewPatientList} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-medium flex items-center hover:bg-gray-200">
                  <List className="h-3 w-3 mr-1" />
                  Patient List
                </button>
                <button className="bg-red-100 text-red-800 px-3 py-1 rounded-lg text-xs font-medium flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {systemMetrics.criticalAlerts} Alerts
                </button>
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                    {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                  </div>
                  <div className="ml-2 text-sm">
                    <div className="font-medium">{user?.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-500">{user?.role || 'Physician'}</div>
                  </div>
                </div>
                <button onClick={onLogout} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-medium flex items-center hover:bg-gray-200">
                  <LogOut className="h-3 w-3 mr-1" />
                  End Shift
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      
      {/* Metrics Bar */}
      {showMetrics && (
        <div className="bg-gray-800 text-white px-4 py-2 text-xs">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              {[
                { icon: Users, label: 'ED Census', value: totalPatients || systemMetrics.currentPatients },
                { icon: Bed, label: 'Beds', value: systemMetrics.bedOccupancy },
                { icon: Clock, label: 'Avg Wait', value: systemMetrics.avgWaitTime },
                { icon: Brain, label: 'AI Response', value: systemMetrics.responseTime, color: 'text-blue-400' },
                { icon: Target, label: 'Calibration', value: systemMetrics.riskCalibration, color: 'text-green-400' },
                { icon: Database, label: 'Source', value: systemMetrics.dataSource, color: 'text-purple-400' }
              ].map(({ icon: Icon, label, value, color = 'text-gray-400' }, idx) => (
                <div key={idx} className="flex items-center">
                  <Icon className={`h-3 w-3 mr-1 ${color}`} />
                  <span className="text-gray-400 mr-1">{label}:</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-xs text-gray-400">Last Update: {systemMetrics.lastUpdate}</div>
              <button onClick={() => setShowMetrics(false)} className="text-gray-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Patient Selection for Main Workstation */}
          {isMainWorkstation && (
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Active Patient</h3>
                <button 
                  onClick={() => setShowPatientSelector(!showPatientSelector)}
                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                >
                  Change Patient
                </button>
              </div>
              {showPatientSelector && (
                <div className="mb-3 max-h-48 overflow-y-auto bg-white border rounded-lg">
                  {loading ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-4 animate-spin mx-auto" />
                    </div>
                  ) : patients.length > 0 ? (
                    patients.slice(0, 10).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          onPatientSelect(p);
                          setShowPatientSelector(false);
                        }}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                          patient?.id === p.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div>
                          <div className="text-sm font-medium">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.condition}</div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          p.severity === 'Critical' ? 'bg-red-100 text-red-800' :
                          p.severity === 'High' ? 'bg-orange-100 text-orange-800' :
                          p.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {p.severity}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No patients available
                    </div>
                  )}
                  {patients.length > 10 && (
                    <button
                      onClick={onViewPatientList}
                      className="w-full px-3 py-2 text-center text-sm text-blue-600 hover:bg-gray-50"
                    >
                      View all {totalPatients} patients →
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Patient Header */}
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  patient ? getESIColor(esiLevel) : 'bg-gray-300 text-gray-600'
                }`}>
                  {patient ? esiLevel : '?'}
                </div>
                <div className="ml-3">
                  <h3 className="font-bold text-gray-900">
                    {patient ? getPatientName() : 'Select a Patient'}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {patient ? (
                      <>
                        {getPatientAge() !== '--' ? `${getPatientAge()}y` : '--'} {patient?.gender || '--'} • MRN: {getMRN()}
                      </>
                    ) : (
                      'No patient selected'
                    )}
                  </p>
                </div>
              </div>
              <div className="text-right text-xs">
                <div className="font-medium text-gray-900">
                  {patient ? (fhirData.encounter.location?.[0]?.location?.display || 'No Bed Assigned') : '--'}
                </div>
                <div className="text-gray-500">{new Date().toLocaleTimeString()}</div>
              </div>
            </div>
            
            {patient && (
              <>
                {/* Metrics */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-white p-2 rounded border text-center">
                    <div className="text-xs text-gray-500">Risk Score</div>
                    <div className={`text-sm font-bold px-2 py-1 rounded ${getRiskColor(riskScore)}`}>
                      {riskScore ? (riskScore * 100).toFixed(1) + '%' : '--'}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border text-center">
                    <div className="text-xs text-gray-500">AI Confidence</div>
                    <div className="text-sm font-bold text-green-700">{getAIConfidence() ? `${getAIConfidence()}%` : '--'}</div>
                  </div>
                  <div className="bg-white p-2 rounded border text-center">
                    <div className="text-xs text-gray-500">Time in ED</div>
                    <div className="text-sm font-bold text-blue-700">5m</div>
                  </div>
                </div>
                
                {/* Chief Complaint */}
                <div className="bg-white p-3 rounded border">
                  <div className="text-xs font-medium text-gray-700 mb-1">Chief Complaint</div>
                  <div className="text-sm text-gray-900">{patient?.condition || 'No complaint recorded'}</div>
                </div>
              </>
            )}
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 text-sm">
            {navItems.map(({ id, icon: Icon, label, badge, badgeColor }) => (
              <button 
                key={id} 
                onClick={() => setActiveTab(id)} 
                disabled={!patient}
                className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                  activeTab === id ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600' : 
                  patient ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                <Icon className="h-4 w-4 mr-3" />
                {label}
                {badge && patient && <div className={`ml-auto ${badgeColor} text-white text-xs px-2 py-0.5 rounded-full`}>{badge}</div>}
              </button>
            ))}
          </nav>
          
          {/* Quick Actions */}
          <div className="p-3 border-t border-gray-200">
            <div className="space-y-2">
              {[
                { 
                  onClick: performAIRiskAnalysis, 
                  icon: isLoading ? Loader2 : Brain, 
                  label: 'Run AI Analysis', 
                  className: 'bg-blue-600 hover:bg-blue-700', 
                  iconClass: isLoading ? 'animate-spin' : '' 
                },
                { icon: AlertTriangle, label: 'Emergency Consult', className: 'bg-red-600 hover:bg-red-700' },
                { icon: FileText, label: 'Generate Summary', className: 'bg-gray-600 hover:bg-gray-700' }
              ].map(({ onClick = () => {}, icon: Icon, label, className, iconClass = '' }, idx) => (
                <button 
                  key={idx} 
                  onClick={onClick} 
                  disabled={isLoading || !patient} 
                  className={`w-full ${className} text-white py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center disabled:bg-gray-400 transition`}
                >
                  <Icon className={`h-4 w-4 mr-2 ${iconClass}`} />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 max-w-6xl mx-auto">
            {!patient ? (
              <div className="flex items-center justify-center min-h-96">
                <div className="text-center">
                  <User className="h-24 w-24 text-gray-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-600 mb-2">Welcome to Your Clinical Workstation</h2>
                  <p className="text-gray-500 mb-4">Select a patient from the sidebar to begin clinical assessment</p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                    <div className="text-sm text-blue-700"><strong>Emergency Department EHR</strong></div>
                    <div className="text-xs text-blue-600 mt-1">
                      {totalPatients} patients in system • AI-enhanced clinical support
                    </div>
                  </div>
                  {isMainWorkstation && (
                    <button 
                      onClick={onViewPatientList}
                      className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      View Patient List
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                      <Brain className="h-6 w-6 mr-2 text-blue-600" />
                      AI-Enhanced Emergency Assessment
                    </h1>
                    <p className="text-sm text-gray-500">
                      FHIR R4 Integration • {dataSourceConfig[dataSource].name} • Generated {currentTime.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => fetchPatientData(patient?.id)} 
                      className="bg-white border border-gray-300 hover:bg-gray-50 rounded-lg px-4 py-2 text-sm font-medium flex items-center"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                      Refresh Data
                    </button>
                    <button className="bg-white border border-gray-300 hover:bg-gray-50 rounded-lg px-4 py-2 text-sm font-medium flex items-center">
                      <Download className="h-4 w-4 mr-2" />
                      Export Report
                    </button>
                    <button 
                      disabled={!fhirData.riskAssessment.performedDateTime} 
                      className="bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 rounded-lg px-4 py-2 text-sm font-medium flex items-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve & Sign
                    </button>
                  </div>
                </div>
                
                {/* Risk Alert */}
                {riskScore > 0.075 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <AlertTriangle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-red-800">HIGH PRIORITY PATIENT</h3>
                        <p className="text-sm text-red-700 mt-1">
                          AI Risk Score: <strong>{(riskScore * 100).toFixed(1)}%</strong> (Above 7.5% intervention threshold) • 
                          Immediate clinical evaluation recommended based on calibrated risk analysis
                        </p>
                        <div className="mt-3 flex items-center space-x-4">
                          <button className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 flex items-center">
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Message Surgery
                          </button>
                          <button className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50 flex items-center">
                            <Timer className="h-4 w-4 mr-2" />
                            Start Timer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Content Tabs */}
                {activeTab === 'ai-analysis' && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                    <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                      <h2 className="font-medium text-gray-800 flex items-center">
                        <Brain className="h-4 w-4 mr-2 text-blue-600" />
                        AI Clinical Analysis
                        {isLoading && <Loader2 className="h-4 w-4 ml-2 animate-spin" />}
                      </h2>
                    </div>
                    <div className="p-4">
                      {!fhirData.riskAssessment.performedDateTime ? (
                        <div className="text-center py-6">
                          <Brain className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <h3 className="text-base font-medium text-gray-600 mb-2">AI Analysis Pending</h3>
                          <p className="text-sm text-gray-500 mb-3">
                            Click "Run AI Analysis" to generate risk assessment and clinical recommendations
                          </p>
                          <button 
                            onClick={performAIRiskAnalysis} 
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center mx-auto"
                          >
                            <Brain className="h-4 w-4 mr-2" />
                            Run AI Analysis
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="border-l-4 border-blue-500 pl-4">
                            <h3 className="font-medium text-gray-800">Risk Assessment Summary</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {fhirData.riskAssessment.prediction[0].rationale}
                            </p>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-gray-50 p-3 rounded">
                              <div className="text-xs text-gray-500">Risk Score</div>
                              <div className="text-lg font-bold text-gray-900">
                                {(riskScore * 100).toFixed(1)}%
                              </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded">
                              <div className="text-xs text-gray-500">Confidence</div>
                              <div className="text-lg font-bold text-gray-900">
                                {getAIConfidence()}%
                              </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded">
                              <div className="text-xs text-gray-500">Analysis Time</div>
                              <div className="text-lg font-bold text-gray-900">
                                {systemMetrics.aiProcessingTime}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {activeTab === 'fhir-data' && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                    <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-blue-50 border-b border-gray-200">
                      <h2 className="font-semibold text-gray-800 flex items-center">
                        <Database className="h-5 w-5 mr-2 text-green-600" />
                        FHIR R4 Data Structure
                      </h2>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {[
                          { title: 'Patient Resource', icon: User, data: fhirData.patient },
                          { title: 'Encounter Resource', icon: Bed, data: fhirData.encounter },
                          { title: 'Risk Assessment Resource', icon: Target, data: fhirData.riskAssessment }
                        ].map(({ title, icon: Icon, data }, idx) => (
                          <div key={idx} className="border border-gray-200 rounded-lg p-4">
                            <h3 className="font-medium text-gray-800 mb-2 flex items-center">
                              <Icon className="h-4 w-4 mr-2" />
                              {title}
                            </h3>
                            <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto">
                              {JSON.stringify(data, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Placeholder for other tabs */}
                {['orders', 'lab-results', 'imaging', 'procedures', 'history'].includes(activeTab) && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                    <div className="p-12 text-center">
                      <div className="text-gray-400 mb-2">
                        {navItems.find(item => item.id === activeTab)?.icon && 
                          React.createElement(navItems.find(item => item.id === activeTab).icon, { className: 'h-12 w-12 mx-auto' })
                        }
                      </div>
                      <h3 className="text-lg font-medium text-gray-600 mb-2">
                        {navItems.find(item => item.id === activeTab)?.label}
                      </h3>
                      <p className="text-sm text-gray-500">
                        This section will be available in Phase 2 of the clinical integration
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Footer */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-800">FHIR R4 Integration Status</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    This dashboard is configured for {dataSourceConfig[dataSource].name} integration with full FHIR R4 compliance. 
                    All patient data follows healthcare interoperability standards with HIPAA-compliant security measures.
                  </p>
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      {[
                        { icon: Shield, color: 'text-green-500', label: 'HIPAA Compliant' },
                        { icon: Database, color: 'text-blue-500', label: 'FHIR R4' },
                        { icon: Cloud, color: 'text-purple-500', label: dataSourceConfig[dataSource].name },
                        { icon: Brain, color: 'text-orange-500', label: 'AI-Enhanced' }
                      ].map(({ icon: Icon, color, label }, idx) => (
                        <div key={idx} className="flex items-center">
                          <Icon className={`h-3 w-3 mr-1 ${color}`} />
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs">
                      <span className="text-gray-500">Session ID:</span>
                      <span className="font-mono ml-1">{sessionId}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EDDashboardV3; 